import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../store';
import { loginStart, loginFailure } from '../../../store/slices/authSlice';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'B2B' | 'B2C' | 'ADMIN';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRole 
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isAuthenticated, user, token, isLoading } = useSelector((state: RootState) => state.auth);

  useEffect(() => {
    // Validate token on mount
    if (token && !isAuthenticated) {
      dispatch(loginStart());
      // In a real app, you would validate the token here
      // For now, we'll just mark it as failed if no user data
      if (!user) {
        dispatch(loginFailure('Session expired. Please login again.'));
      }
    }
  }, [dispatch, token, isAuthenticated, user]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div className="protected-route__loading">
        <div className="loading-spinner"></div>
        <p>Verifying authentication...</p>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !token) {
    return <Navigate to="/auth/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <div className="protected-route__unauthorized">
        <h2>Access Denied</h2>
        <p>You don't have permission to access this page.</p>
        <button onClick={() => window.history.back()}>
          Go Back
        </button>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
